/**
 * The type scale for a MESSAGE composer — the two textareas a user writes prose into
 * before it becomes part of a support thread.
 *
 * ## What was wrong
 *
 * Measured on `/support/:id` before this: the reply textarea computed to
 * **16px / 23px**, while every other piece of text on the page — the request body, each
 * reply's body, the "From:"/"Page:"/"Browser:" context lines, the field's own label —
 * computed to **14px**. The textarea was, literally, the largest type on the page.
 *
 * The intake's textarea measured **identically** (16px / 23px): they are the same
 * `@repo/ui` `Input` with the same props, so this was never a drift between the two. It
 * is the shared default, showing up on the one surface where it reads wrong.
 *
 * ## Why 14 here and 16 everywhere else
 *
 * `@repo/ui`'s `Input` sets `fontSize: 16` deliberately, and that default is NOT changed
 * by this file — 16px is the design system's field-VALUE size across the whole platform
 * ("value text is 16 because it is what the user typed; a label is 14 and helper copy is
 * 12"). Every `TextField` in the app stays at 16.
 *
 * The support composers are the exception because they are not really form fields. What
 * you type into a name field stays a field value; what you type into this box becomes a
 * MESSAGE, rendered back at `body3`/14px two inches above the box you typed it in. The
 * adjacency is the argument: nobody ever sees the reply box next to the product form, but
 * everybody sees it directly under the thread it is about to join, and a composer that
 * types at 16 and renders at 14 is a visible size change on send.
 *
 * The cost, stated rather than hidden: iOS Safari zooms the viewport when a focused input
 * is under 16px. `apps/platform` is a desktop admin console (§37 — Platform.* verifies at
 * 1280×800) and has no mobile layout, so this does not bite today. It WOULD bite if these
 * composers were ever reused in `apps/app`, which is mobile-first — do not export this
 * there.
 *
 * ## Why a constant and not two `sx` literals
 *
 * Both composers must move together or the fix creates the very drift it removes: the
 * intake modal has the same property (its textarea is the largest type in a modal whose
 * other content is all 14px), so fixing only the reply would leave two boxes doing the
 * same job at two sizes. One value, two call sites (§31 — one source for field chrome).
 *
 * If a third message-composer surface ever appears, promote this to a `size` variant on
 * `@repo/ui`'s `Input` rather than adding a third importer.
 */
export const MESSAGE_FIELD_SX = {
  /**
   * 14px, matching `body3` — the size a message renders at once it is posted.
   *
   * `lineHeight` is deliberately NOT set alongside it. MUI's `InputBase` gives the input
   * slot `1.4375em`, which is relative, so it follows the font down on its own: 23px at
   * 16px becomes ~20.1px at 14px. Pinning it to the thread's 1.5 would gain 0.9px per
   * line and cost the textarea a hardcoded number to keep in step with `body3`.
   */
  fontSize: 14,
}
